import { useQuery } from "@tanstack/react-query";
import { AxiosError } from "axios";
import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { SurveyService } from "../api/SurveyService";
import { WorkplaceCultureService } from "../api/WorkplaceCultureService";
import { ChapterOverview } from "../components/ChapterOverview.tsx/ChapterOverview";
import { SurveyDemographics } from "../components/SurveyDemographics/SurveyDemographics";
import { WorkplaceCultureOverview } from "../components/WorkplaceCultureOverview/WorkplaceCultureOverview";
import { LoadingIndicator } from "../components/ui/LoadingIndicator/LoadingIndicator";
import SurveyDispatch from "../models/surveyDispatch";
import { GetSurveyDispatchResponse } from "../types/survey";
import {
  GetSurveyChapterInsightResponse,
  GetSurveyChapterScoringResponse,
  GetSurveyDemographicResponse,
  GetSurveyScoringDemographicResponse,
} from "../types/workplaceCulture";
import { OVERVIEW_DEMOGRAPHIC_GROUP_ID } from "../utils/WorkplaceCulture.utils";

export const WorkplaceCulturePage = () => {
  const surveyService = new SurveyService();
  const workplaceCultureService = new WorkplaceCultureService();
  const [surveyDispatch, setSurveyDispatch] = useState<SurveyDispatch>();
  const [selectedDemographicFilter, setSelectedDemographicFilter] =
    useState<string>(OVERVIEW_DEMOGRAPHIC_GROUP_ID);

  const { dispatchId } = useParams();
  const navigate = useNavigate();

  const {
    data: surveyDispatchData,
    isLoading: surveyDispatchLoading,
    error: surveyDispatchError,
  } = useQuery<GetSurveyDispatchResponse, AxiosError>(
    [`surveyDispatch-${dispatchId}`],
    () => surveyService.getDispatch(dispatchId as string)
  );

  useEffect(() => {
    if (surveyDispatchData) {
      setSurveyDispatch(new SurveyDispatch(surveyDispatchData));
    }
  }, [surveyDispatchData]);

  const {
    data: surveyDemographicsData,
    isLoading: demographicsLoading,
    error: demographicsError,
  } = useQuery<GetSurveyDemographicResponse, AxiosError>(
    [`surveyDemographics-${surveyDispatchData?.id}`],
    () =>
      workplaceCultureService.getSurveyDemographics(
        surveyDispatchData?.id || ""
      ),
    {
      enabled: !!surveyDispatchData?.id,
    }
  );

  const {
    data: surveyScoringData,
    isLoading: surveyScoringLoading,
    error: surveyScoringError,
    refetch: surveyScoringRefetch,
  } = useQuery<GetSurveyScoringDemographicResponse, AxiosError>(
    [
      [
        `surveyScoring-${surveyDispatchData?.id}`,
        { filterId: selectedDemographicFilter },
      ],
    ],
    () =>
      workplaceCultureService.getSurveyScoring(
        surveyDispatchData?.id || "",
        selectedDemographicFilter
      ),
    {
      enabled: !!surveyDispatchData?.id,
    }
  );

  const {
    data: surveyChapterScoringData,
    isLoading: surveyChapterScoringLoading,
    error: surveyChapterScoringError,
  } = useQuery<GetSurveyChapterScoringResponse, AxiosError>(
    [
      `surveyChapterScoring-${surveyDispatchData?.id}`,
      { filterId: selectedDemographicFilter },
    ],
    () =>
      workplaceCultureService.getSurveyChapterScoring(
        surveyDispatchData?.id || "",
        selectedDemographicFilter
      ),
    {
      enabled: !!surveyDispatchData?.id,
    }
  );

  const { data: surveyChapterInsightsData } = useQuery<
    GetSurveyChapterInsightResponse,
    AxiosError
  >(
    [`surveyChapterInsights-${surveyDispatchData?.id}`],
    () =>
      workplaceCultureService.getSurveyChapterInsights(
        surveyDispatchData?.id || ""
      ),
    {
      enabled: !!surveyDispatchData?.id,
    }
  );

  useEffect(() => {
    if (
      surveyDispatchError?.response?.status === 404 ||
      surveyDispatchError?.response?.status === 400
    ) {
      navigate("/404");
    }
  }, [navigate, surveyDispatchError]);

  const handleSurveyScoringRefetch = (filterId: string) => {
    setSelectedDemographicFilter(filterId);
    surveyScoringRefetch();
  };

  return (
    <>
      {surveyDispatchLoading && <LoadingIndicator />}
      {!surveyDispatchLoading && !surveyDispatchError && (
        <>
          <WorkplaceCultureOverview
            scoringData={surveyScoringData}
            isLoading={surveyScoringLoading}
            error={!!surveyScoringError}
            insightsData={surveyChapterInsightsData}
            hasEnoughAnswers={surveyScoringData?.scores.total_score !== null}
            onScoringRefetch={handleSurveyScoringRefetch}
            selectedDemographicFilter={selectedDemographicFilter}
          />

          <ChapterOverview
            dispatchId={surveyDispatchData?.id}
            chapterScoringData={surveyChapterScoringData}
            isLoading={surveyChapterScoringLoading}
            error={!!surveyChapterScoringError}
            chapterInsightsData={surveyChapterInsightsData}
            surveyClosed={surveyDispatch?.isClosed()}
          />

          <SurveyDemographics
            isLoading={demographicsLoading}
            demographicGroups={surveyDemographicsData?.demographic_groups}
            totalEngagement={surveyDemographicsData?.engagement}
            error={!!demographicsError}
          />
        </>
      )}
    </>
  );
};
